import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, ComposedChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { tresorerie as api } from '../services/api';
import { useBi } from '../BiContext';
import FiltresPanel from './FiltresPanel';
import { basculer, usePanneau } from './ventesUtils';
import './Stock.css';   // st-tools / st-seg du réglage d'affichage (CLAUDE.md §2)
import './Ventes.css';   // vt-kpis du bandeau commun
import './Tresorerie.css';
import TresorerieAnalyseIa from './TresorerieAnalyseIa';

// ─── Trésorerie ───────────────────────────────────────────────────────────────
// Trois sections — situation, évolution (solde reconstruit), pont — portant sur
// l'AGRÉGAT des sociétés cochées dans le panneau de filtres. Sélection vide =
// on invite à cocher une société (jamais un écran cassé silencieux).
//
// `branche` choisit le PÉRIMÈTRE lu dans l'entrepôt : 'auto-antilles' (les 5
// sociétés du fichier racine) ou 'groupe' (les 27 sociétés consolidées du
// dossier /GROUPE). L'écran est le même, seules les tables sources changent.

const COULEURS = ['#2E5BFF', '#C9A84C', '#0F766E', '#9D174D', '#4D7C0F', '#B45309', '#475569'];
const eur = (v) => Math.round(v || 0).toLocaleString('fr-FR') + ' €';
const M = (v) => (v / 1e6).toLocaleString('fr-FR', { maximumFractionDigits: 1 }) + ' M€';
const Msign = (v) => (v >= 0 ? '+' : '') + M(v);
const K = (v) => Math.round((v || 0) / 1e3).toLocaleString('fr-FR') + ' K€';   // tableau par société
const jjmm = (d) => (d ? d.slice(5).split('-').reverse().join('/') : '');
const jjmmaa = (d) => (d ? d.split('-').reverse().join('/') : '');

// ─── Les bandes du graphique intégré ────────────────────────────────────────
// Une seule entrée, alimente la courbe intégrée, sa légende et le pont : sans
// ça, la couleur d'« Impôts » finissait par différer d'un bloc à l'autre.
// Une seule famille alimente (vert), quatre consomment (teintes chaudes
// distinctes) : le sens de lecture se voit avant les chiffres.
// ⚠ LE PROBLÈME D'ÉCHELLE, et sa résolution.
// Sur six mois les encaissements clients font +79,5 M€ et les fournisseurs
// −78,5 M€ : deux courbes quasi symétriques. Empilées telles quelles, l'axe
// couvre 170 M€ et la variation réelle — −7,8 M€ — devient un trait plat sur
// zéro. On voyait le volume, jamais la cause.
// Les bandes portent donc le CYCLE D'EXPLOITATION NET (clients − fournisseurs),
// puis les postes qui ne se compensent pas. L'axe tient alors en ±8 M€ et la
// question « pourquoi » se lit. Le détail brut reste sous le graphique et dans
// le pont de la section 3 : rien n'est masqué, c'est l'échelle qui change.
// ⚠ Trois bandes, pas cinq, et « Emprunts » est fondu dans « Financement &
// frais ». Raison technique autant que visuelle : `stackOffset="sign"` range
// les ZÉROS du côté positif. Une bande nulle jusqu'au premier remboursement
// démarrait donc en haut de la pile positive puis plongeait d'un coup sous
// zéro — un éclat diagonal en travers du graphique. Les frais bancaires, eux,
// courent dès le premier jour : la bande fusionnée n'est jamais nulle, et le
// défaut disparaît par construction. Le détail poste par poste reste dans le
// pont de la section 3, qui n'a pas ce problème d'échelle.
const BANDES = [
  { k: 'cycle',   label: "Cycle d'exploitation",  c: '#0F766E', aide: 'encaissements clients − fournisseurs & salaires',
    detail: 'fournisseurs', detailLabel: 'Fournisseurs & salaires' },
  { k: 'impots',  label: 'Impôts & cotisations',  c: '#9D174D', detail: 'impots', detailLabel: 'Impôts & cotisations' },
  { k: 'autres',  label: 'Financement & frais',   c: '#B45309', aide: "remboursements d'emprunts, frais bancaires, impayés",
    detail: 'emprunts,frais', detailLabel: 'Financement & frais' },
];

// ── Pont de trésorerie (waterfall) en SVG ──
function Waterfall({ b }) {
  const fraisdiv = b.frais + b.divers;
  const FAM = [
    { k: 'Pdebut', label: 'Position début', type: 'abs' },
    { k: 'clients', label: 'Encaiss. clients', type: 'flow' },
    { k: 'fournisseurs', label: 'Fourn. & salaires', type: 'flow' },
    { k: 'impots', label: 'Impôts & cotis.', type: 'flow' },
    { k: 'emprunts', label: 'Emprunts', type: 'flow' },
    { k: 'fraisdiv', label: 'Frais & divers', type: 'flow' },
    { k: 'Pnow', label: 'Position fin', type: 'abs' },
  ];
  const val = { ...b, fraisdiv };
  let cum = 0; const bars = [];
  for (const f of FAM) {
    if (f.type === 'abs') { bars.push({ label: f.label, y0: 0, y1: val[f.k], col: '#64748b', v: val[f.k], abs: true }); cum = val[f.k]; }
    else { const nv = cum + val[f.k]; bars.push({ label: f.label, y0: cum, y1: nv, col: val[f.k] >= 0 ? '#0F766E' : '#b4321f', v: val[f.k] }); cum = nv; }
  }
  const allY = bars.flatMap((x) => [x.y0, x.y1]).concat(0);
  let min = Math.min(...allY), max = Math.max(...allY); const range = (max - min) || 1; min -= range * 0.06; max += range * 0.12;
  const W = 620, H = 320, PL = 54, PR = 12, PT = 14, PB = 52; const bw = (W - PL - PR) / bars.length;
  const y = (v) => PT + (1 - (v - min) / (max - min)) * (H - PT - PB);
  const y0 = y(0);
  const grid = []; for (let k = 0; k <= 4; k++) { const vv = min + (max - min) * k / 4, yy = y(vv); grid.push({ yy, label: M(vv) }); }
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Pont de trésorerie">
      {grid.map((g, i) => <line key={'g' + i} x1={PL} y1={g.yy} x2={W - PR} y2={g.yy} stroke="var(--border-default,#e2e8f0)" />)}
      {grid.map((g, i) => <text key={'yl' + i} x={PL - 6} y={g.yy + 3} textAnchor="end" fontSize="9.5" fill="var(--text-muted,#96a0b0)">{g.label}</text>)}
      <line x1={PL} y1={y0} x2={W - PR} y2={y0} stroke="var(--text-secondary,#657085)" strokeDasharray="4 3" />
      {bars.map((bar, i) => {
        const cx = PL + i * bw + bw * 0.16, w = bw * 0.68;
        const top = y(Math.max(bar.y0, bar.y1)), h = Math.max(2, Math.abs(y(bar.y0) - y(bar.y1)));
        const vy = bar.abs ? (bar.y1 < 0 ? y(bar.y1) + 13 : y(bar.y1) - 5) : (top - 5);
        const words = bar.label.split(' ');
        return (
          <g key={i}>
            {i < bars.length - 1 && <line x1={cx + w} y1={y(bar.y1)} x2={PL + (i + 1) * bw + bw * 0.16} y2={y(bar.y1)} stroke="var(--text-muted,#96a0b0)" strokeDasharray="2 2" />}
            <rect x={cx} y={top} width={w} height={h} fill={bar.col} rx="2" opacity={bar.abs ? 1 : 0.92} />
            <text x={cx + w / 2} y={vy} textAnchor="middle" fontSize="10" fontWeight="700" fill={bar.col}>{bar.abs ? M(bar.v) : Msign(bar.v)}</text>
            <text x={cx + w / 2} y={H - 32} textAnchor="middle" fontSize="9.5" fill="var(--text-secondary,#657085)">{words.slice(0, 2).join(' ')}</text>
            {words.length > 2 && <text x={cx + w / 2} y={H - 21} textAnchor="middle" fontSize="9.5" fill="var(--text-secondary,#657085)">{words.slice(2).join(' ')}</text>}
          </g>
        );
      })}
    </svg>
  );
}

export default function Tresorerie({ branche = 'auto-antilles' }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  // Réglage d'affichage ⇒ en-tête de carte (CLAUDE.md §2), pas le panneau.
  const [vue, setVue] = useState('pourquoi');
  // Analyse IA (2026-09-30) : Auto Antilles seulement pour l'instant.
  const [ia, setIa] = useState(false);
  const { ia: droitIa } = useBi(); // droit « Analyse IA » de la personne connectée
  const [detail, setDetail] = useState(null);      // { famille, postes, lignes… }
  const [detailCharge, setDetailCharge] = useState(false);
  const [posteOuvert, setPosteOuvert] = useState(null);
  // Le panneau commun : replié d'office sur téléphone (ventesUtils.js).
  const [panneauOuvert, togglePanneau] = usePanneau();

  useEffect(() => {
    let actif = true;
    setData(null);
    setErreur('');
    api.charger(branche).then((r) => { if (actif) setData(r.data); }).catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [branche]);

  const societesDispo = useMemo(() => data?.societes || [], [data]);
  const couleurDe = useMemo(() => {
    const m = {}; societesDispo.forEach((s, i) => { m[s.code] = COULEURS[i % COULEURS.length]; }); return m;
  }, [societesDispo]);

  // Sélection depuis l'URL : vide = toutes, '__aucune__' = aucune.
  const societesUrl = params.get('societes') || '';
  const selected = useMemo(() => {
    if (societesUrl === '__aucune__') return [];
    if (!societesUrl) return societesDispo.map((s) => s.code);
    return societesUrl.split(',').filter(Boolean);
  }, [societesUrl, societesDispo]);
  const selSet = new Set(selected);

  const setFiltre = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set('societes', v); else next.delete('societes');
    setParams(next, { replace: true });
  };
  const toggleSociete = (code) => {
    const suivantes = selSet.has(code) ? selected.filter((c) => c !== code) : [...selected, code];
    setFiltre(suivantes.length === 0 ? '__aucune__'
      : suivantes.length === societesDispo.length ? '' : suivantes.join(','));
  };

  const nbActifs = societesUrl ? 1 : 0;

  // Agrégats de la sélection.
  const sel = societesDispo.filter((s) => selSet.has(s.code));
  const agg = (f) => sel.reduce((t, s) => t + (s[f] || 0), 0);
  const serieAgg = useMemo(() => (data?.serie || []).map((p) => ({ d: p.d, v: selected.reduce((t, c) => t + (p[c] || 0), 0) })), [data, selected]);
  const bridgeAgg = useMemo(() => {
    const keys = ['clients', 'fournisseurs', 'impots', 'emprunts', 'frais', 'divers', 'net'];
    const b = Object.fromEntries(keys.map((k) => [k, 0]));
    let Pnow = 0;
    for (const c of selected) { const bc = data?.bridge?.[c]; if (bc) { keys.forEach((k) => { b[k] += bc[k]; }); Pnow += bc.Pnow; } }
    return { ...b, Pnow, Pdebut: Pnow - b.net, cycle: b.clients + b.fournisseurs };
  }, [data, selected]);

  // Le poste qui pèse le plus, désigné par les chiffres et non écrit en dur.
  // La version précédente nommait toujours « les impôts », ce qui restait vrai
  // par hasard et cessait de l'être dès qu'on filtrait une société.
  const cause = useMemo(() => {
    const cand = [
      { label: "le cycle d'exploitation", v: bridgeAgg.cycle },
      { label: 'les impôts & cotisations', v: bridgeAgg.impots },
      { label: 'les emprunts', v: bridgeAgg.emprunts },
      { label: 'les frais & divers', v: bridgeAgg.frais + bridgeAgg.divers },
    ].filter((c) => c.v < 0).sort((a, b) => a.v - b.v);
    return cand[0] || null;
  }, [bridgeAgg]);

  // Contributions cumulées de la sélection : une ligne par point, le cumul de
  // chaque famille depuis le début de la fenêtre. `frais` absorbe `divers`
  // (quelques milliers d'euros sur six mois) pour ne pas ajouter une cinquième
  // bande illisible.
  const contribAgg = useMemo(() => {
    const pts = data?.contributions?.[selected[0]] || [];
    return pts.map((_, i) => {
      const e = { d: pts[i].d, net: 0, clients: 0, fournisseurs: 0, impots: 0, emprunts: 0, frais: 0 };
      for (const c of selected) {
        const p = data?.contributions?.[c]?.[i];
        if (!p) continue;
        e.clients += p.clients; e.fournisseurs += p.fournisseurs;
        e.impots += p.impots; e.emprunts += p.emprunts;
        e.frais += p.frais + p.divers; e.net += p.net;
      }
      e.cycle = e.clients + e.fournisseurs;
      e.autres = e.emprunts + e.frais;
      return e;
    });
  }, [data, selected]);

  // Le poste ouvert vit dans l'URL : il se partage, et il se reflète dans le
  // panneau comme n'importe quel filtre (CLAUDE.md §4 bis).
  const posteUrl = params.get('poste') || '';
  const bandeOuverte = BANDES.find((b) => b.detail === posteUrl) || null;

  useEffect(() => {
    if (!posteUrl) { setDetail(null); return undefined; }
    let actif = true;
    setDetailCharge(true); setPosteOuvert(null);
    api.detail({ famille: posteUrl, branche, societes: societesUrl })
      .then((r) => { if (actif) { setDetail(r.data); setDetailCharge(false); } })
      .catch(() => { if (actif) { setDetail(null); setDetailCharge(false); } });
    return () => { actif = false; };
  }, [posteUrl, branche, societesUrl]);

  // Deux points d'entrée — la légende du graphique et la ventilation de la
  // section 3 — mais UN seul état, dans l'URL (CLAUDE.md §4 bis).
  const ouvrirPoste = (famille) => {
    const next = new URLSearchParams(params);
    if (posteUrl === famille) next.delete('poste'); else next.set('poste', famille);
    setParams(next, { replace: true });
  };
  const ouvrirBande = (b) => ouvrirPoste(b.detail);

  const vide = selected.length === 0;
  const tp = agg('pos'), temp = agg('emprunt');
  const arrete = data?.arrete ? jjmmaa(data.arrete.slice(0, 10)) : '—';
  const lastV = serieAgg.length ? serieAgg[serieAgg.length - 1].v : 0;
  const courbeCol = selected.length === 1 ? (lastV < 0 ? '#b4321f' : '#0F766E') : '#64748b';
  const groupeEntier = selected.length === societesDispo.length;
  const nomSel = groupeEntier ? 'Le groupe' : selected.length === 1 ? (data?.bridge?.[selected[0]]?.nom || sel[0]?.nom) : `${selected.length} sociétés`;
  const sujet = groupeEntier ? 'le groupe' : nomSel;

  return (
    <>
      <main className="tr-page">
        {erreur ? (
          <div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div>
        ) : !data ? (
          <div className="tr-skel" />
        ) : (
          <>
            <div className="tr-head">
              <div><h1>Trésorerie{branche === 'groupe' ? ' · Groupe' : ''}</h1></div>
              <div className="tr-tete-d">
                {branche === 'auto-antilles' && droitIa !== 'aucune' && (
                  <button type="button" className="tr-ia" onClick={() => setIa(true)}
                    title="Analyse IA de la trésorerie : évolution, explication des variations, fonds de roulement, fournisseurs, recommandations — données pseudonymisées">Analyse IA</button>
                )}
                <div className="tr-asof">Arrêté au <b>{arrete}</b></div>
              </div>
            </div>

            {vide ? (
              <div className="bi-empty" style={{ marginTop: 20 }}><h2>Aucune société sélectionnée</h2><p>Cochez au moins une société dans les filtres à droite.</p></div>
            ) : (
              <>
                {/* Bandeau d'indicateurs du gabarit commun (CLAUDE.md §3) :
                    la Trésorerie était le seul rapport à n'en avoir aucun, avec
                    deux tuiles maison à la place. */}
                <div className="vt-kpis" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
                  <div className="vt-kpi">
                    <div className="l">{selected.length === societesDispo.length ? 'Position nette vis-à-vis du pool' : selected.length === 1 ? `Position de ${sel[0].nom}` : `Position (${selected.length} sociétés)`}</div>
                    <div className="v" style={{ color: tp < 0 ? 'var(--tr-neg)' : 'var(--tr-pos)' }}>{M(tp)}</div>
                    <div className="d">ce que les sociétés doivent, net, au pool de la holding</div>
                  </div>
                  <div className="vt-kpi">
                    <div className="l">Emprunts en cours</div>
                    <div className="v">{M(temp)}</div>
                    <div className="d">dette bancaire — à part de la trésorerie</div>
                  </div>
                  <div className="vt-kpi">
                    <div className="l">Encaissements + caisse</div>
                    <div className="v">{M(agg('buffer'))}</div>
                    <div className="d">tampon hors pooling</div>
                  </div>
                </div>
                <div className="tr-card tr-tbl-card"><div className="tr-tbl-wrap">
                  <table className="tr-tbl">
                    <thead><tr><th className="l">Société <span className="tr-unit">en K€ · position = compte courant de pooling, négatif = emprunte au pool</span></th><th>Position trésorerie</th><th>dont cash pooling</th><th>dont encaiss.+caisse</th><th>Emprunts</th></tr></thead>
                    <tbody>
                      {sel.map((s) => (
                        <tr key={s.code} className={`tbl-clic${societesUrl === s.code ? ' actif' : ''}`}
                            onClick={() => setFiltre(basculer(societesUrl, s.code))}>
                          <td className="l">{s.nom} <span className={`tr-chip ${s.pos < 0 ? 'deb' : 'cred'}`}>{s.pos < 0 ? 'débitrice' : 'créditrice'}</span></td>
                          <td className={`big ${s.pos < 0 ? 'neg' : 'pos'}`}>{K(s.pos)}</td>
                          <td className="dim">{K(s.pool)}</td>
                          <td className="dim">{K(s.buffer)}</td>
                          <td className="dim">{K(s.emprunt)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr><td className="l">TOTAL</td><td className={`big ${tp < 0 ? 'neg' : 'pos'}`}>{K(tp)}</td><td className="dim">{K(agg('pool'))}</td><td className="dim">{K(agg('buffer'))}</td><td className="dim">{K(temp)}</td></tr></tfoot>
                  </table>
                </div></div>

                <div className="tr-card">
                  <div className="tr-card-h">
                    <h2>{vue === 'pourquoi' ? "Pourquoi la trésorerie a bougé" : 'Solde de trésorerie'}</h2>
                    <div className="st-tools">
                      {vue === 'pourquoi' && <span className="cr-hint">cumul depuis le 1<sup>er</sup> jour · la courbe est la variation nette</span>}
                      <div className="st-seg">
                        <button type="button" className={vue === 'pourquoi' ? 'on' : ''} onClick={() => setVue('pourquoi')}>Ce qui l'explique</button>
                        <button type="button" className={vue === 'solde' ? 'on' : ''} onClick={() => setVue('solde')}>Solde</button>
                      </div>
                    </div>
                  </div>
                  <div className="tr-chart">
                    <ResponsiveContainer width="100%" height={320}>
                      {vue === 'solde' ? (
                        <AreaChart data={serieAgg} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default,#e2e8f0)" />
                          <XAxis dataKey="d" tickFormatter={jjmm} fontSize={11} minTickGap={40} />
                          <YAxis tickFormatter={M} width={70} fontSize={11} />
                          <Tooltip formatter={(v) => [eur(v), 'Trésorerie']} labelFormatter={jjmmaa} />
                          <ReferenceLine y={0} stroke="var(--text-secondary,#657085)" strokeDasharray="4 3" />
                          <Area type="monotone" dataKey="v" stroke={courbeCol} strokeWidth={2.2} fill={courbeCol} fillOpacity={0.08} dot={false} />
                        </AreaChart>
                      ) : (
                        <ComposedChart data={contribAgg} stackOffset="sign" margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default,#e2e8f0)" />
                          <XAxis dataKey="d" tickFormatter={jjmm} fontSize={11} minTickGap={40} />
                          <YAxis tickFormatter={M} width={70} fontSize={11} />
                          <Tooltip labelFormatter={jjmmaa}
                            formatter={(v, n) => [Msign(v), n === 'net' ? 'Variation nette' : (BANDES.find((f) => f.k === n) || {}).label || n]} />
                          <ReferenceLine y={0} stroke="var(--text-primary,#1a1a2e)" />
                          {/* Empilement signé : ce qui alimente monte, ce qui consomme
                              descend. Recharts empile les négatifs sous zéro d'eux-mêmes. */}
                          {/* `linear`, pas `monotone` : un cumul est linéaire par morceaux
                              entre deux relevés hebdomadaires. Le lissage inventait des
                              creux et des bosses qui n'ont jamais eu lieu. */}
                          {BANDES.map((f) => (
                            // ⚠ Aucun contour. `stackOffset="sign"` range les ZÉROS du côté
                            // positif : tant qu'une société n'a pas remboursé d'emprunt, la
                            // bande « Emprunts » vaut 0 et son contour longeait le haut de la
                            // pile positive, puis plongeait d'un coup au premier remboursement
                            // — un trait diagonal qui traversait le graphique sans rien dire.
                            // Le remplissage seul, lui, est invisible quand la bande est nulle.
                            <Area key={f.k} type="linear" dataKey={f.k} stackId="f" stroke="none"
                              fill={f.c} fillOpacity={0.6} dot={false}
                              isAnimationActive={false} />
                          ))}
                          <Line type="linear" dataKey="net" stroke="var(--text-primary,#1a1a2e)"
                            strokeWidth={2.4} dot={false} isAnimationActive={false} />
                        </ComposedChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                  <div className="tr-lgd">
                    {vue === 'pourquoi' ? (
                      <>
                        {BANDES.map((f) => {
                          const v = contribAgg.length ? contribAgg[contribAgg.length - 1][f.k] : 0;
                          return (
                            <button type="button" key={f.k} title={f.aide || ''}
                              className={`tr-lgd-b${posteUrl === f.detail ? ' on' : ''}`}
                              onClick={() => ouvrirBande(f)}>
                              <i style={{ background: f.c }} />{f.label} <b>{Msign(v)}</b>
                              <span className="chev">▶</span>
                            </button>
                          );
                        })}
                        <span className="tr-lgd-net"><i className="trait" />Variation nette <b>{Msign(bridgeAgg.net)}</b></span>
                        <span className="tr-lgd-brut">dont, en brut : encaissements clients <b>{Msign(bridgeAgg.clients)}</b> · fournisseurs &amp; salaires <b>{Msign(bridgeAgg.fournisseurs)}</b></span>
                      </>
                    ) : <span>Solde reconstruit, points hebdomadaires.</span>}
                  </div>
                </div>

                {posteUrl && bandeOuverte && (
                  <div className="tr-card tr-detail">
                    <div className="tr-card-h">
                      <h2>{bandeOuverte.detailLabel} — détail</h2>
                      <button type="button" className="tr-x" onClick={() => ouvrirBande(bandeOuverte)}>Fermer ✕</button>
                    </div>
                    {detailCharge && <p className="tr-cap">Chargement…</p>}
                    {!detailCharge && detail && detail.postes.length === 0 && (
                      <p className="tr-cap">Aucun mouvement sur ce périmètre.</p>
                    )}
                    {!detailCharge && detail && detail.postes.length > 0 && (
                      <>
                        <div className="tr-tbl-wrap">
                          <table className="tr-tbl">
                            <thead><tr>
                              <th className="l">Poste</th><th>Mouvements</th><th>Montant</th>
                              <th>Part</th><th>Du</th><th>Au</th>
                            </tr></thead>
                            <tbody>
                              {detail.postes.map((p) => (
                                <tr key={p.poste} className={`tbl-clic${posteOuvert === p.poste ? ' actif' : ''}`}
                                    onClick={() => setPosteOuvert(posteOuvert === p.poste ? null : p.poste)}>
                                  <td className="l">{p.poste}</td>
                                  <td className="dim">{p.n.toLocaleString('fr-FR')}</td>
                                  <td className="big neg">{eur(p.montant)}</td>
                                  <td className="dim">{detail.total ? Math.round((p.montant / detail.total) * 100) : 0} %</td>
                                  <td className="dim">{jjmmaa(p.du)}</td>
                                  <td className="dim">{jjmmaa(p.au)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot><tr>
                              <td className="l">Total</td>
                              <td className="dim">{detail.postes.reduce((t, p) => t + p.n, 0).toLocaleString('fr-FR')}</td>
                              <td className="big neg">{eur(detail.total)}</td>
                              <td /><td /><td />
                            </tr></tfoot>
                          </table>
                        </div>
                        {posteOuvert && (
                          <div className="tr-tbl-wrap tr-lignes">
                            <table className="tr-tbl tbl-dense">
                              <thead><tr>
                                <th className="l">Date</th><th className="l">Société</th>
                                <th className="l">Libellé bancaire</th><th>Montant</th>
                              </tr></thead>
                              <tbody>
                                {detail.lignes.filter((l) => l.poste === posteOuvert).slice(0, 200).map((l, i) => (
                                  <tr key={i}>
                                    <td className="l">{jjmmaa(l.jour)}</td>
                                    <td className="l">{l.societe}</td>
                                    <td className="l tr-lib" title={l.libelle || ''}>{l.libelle || '—'}</td>
                                    <td>{eur(l.montant)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="tr-analysis">
                  <div className="tr-card tr-chart"><Waterfall b={bridgeAgg} /></div>
                  <div className="tr-card tr-story">
                    <h3>{nomSel}</h3>
                    <div className="tr-verdict">
                      Sur 6 mois, {sujet} passe de <b>{M(bridgeAgg.Pdebut)}</b> à <b>{M(bridgeAgg.Pnow)}</b>, soit <b style={{ color: bridgeAgg.net < 0 ? 'var(--tr-neg)' : 'var(--tr-pos)' }}>{Msign(bridgeAgg.net)}</b>.{' '}
                      {cause && <>Le <b>cycle d'exploitation</b> (clients {Msign(bridgeAgg.clients)} moins fournisseurs &amp; salaires {Msign(bridgeAgg.fournisseurs)}) dégage <b>{Msign(bridgeAgg.cycle)}</b> ; ce qui pèse le plus, ce sont <b>{cause.label} ({Msign(cause.v)})</b>.</>}
                    </div>
                    <div className="tr-brk">
                      {[
                        ['Encaissements clients', bridgeAgg.clients, null],
                        ['Fournisseurs & salaires', bridgeAgg.fournisseurs, 'fournisseurs'],
                        ['Impôts & cotisations', bridgeAgg.impots, 'impots'],
                        ['Emprunts', bridgeAgg.emprunts, 'emprunts'],
                        ['Frais & divers', bridgeAgg.frais + bridgeAgg.divers, 'frais'],
                      ].filter((r) => Math.abs(r[1]) > 1000).map(([k, v, fam]) => (
                        <div key={k} className={`tr-brk-row${fam ? ' tbl-clic' : ''}${posteUrl === fam ? ' on' : ''}`}
                          onClick={fam ? () => ouvrirPoste(fam) : undefined}
                          title={fam ? 'Voir le détail' : "Non détaillable : le libellé bancaire porte le nom du payeur"}>
                          <span className="sw" style={{ background: v >= 0 ? 'var(--tr-pos)' : 'var(--tr-neg)' }} />
                          <span className="k">{k}</span>
                          <span className={`v ${v >= 0 ? 'pos' : 'neg'}`}>{Msign(v)}</span>
                          <span className="chev">{fam ? '▶' : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </main>
      {ia && <TresorerieAnalyseIa selection={selected} onFermer={() => setIa(false)} />}

      <FiltresPanel
        ouvert={panneauOuvert}
        onToggle={togglePanneau}
        societesDispo={societesDispo}
        societesActives={selected}
        onToggleSociete={toggleSociete}
        onToutesSocietes={() => setFiltre('')}
        onAucuneSociete={() => setFiltre('__aucune__')}
        couleurDe={couleurDe}
        nbActifs={nbActifs}
        onReset={() => setFiltre('')}
      />
    </>
  );
}
